import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

export interface PlanLimitDialogRequest {
  /** Matches TenantLimit's feature_key convention (e.g. 'transport_units', 'products') — drives
   *  the friendly label + "Manage" route lookup in PlanLimitDialogService.describe(). */
  featureKey: string;
  /** Raw server message (PlanLimitExceededException/FeatureNotAvailableException's own text) —
   *  shown as a fallback only when the featureKey isn't in the lookup table below. */
  message: string;
  used?: number | null;
  limit?: number | null;
}

export interface PlanLimitDialogInfo {
  label: string;
  manageRoute: string | null;
}

// One shared lookup for every module that can 402/403 on a plan/count limit — same "common way"
// principle as the rest of this service: add a row here once, every create flow that already
// funnels through the interceptor's 402/403 handling (see tallysheet-http.interceptor.ts) gets a
// correctly-labelled dialog for free, no per-module dialog copy needed.
//
// 'max-users' is a deliberate duplicate of 'team_members' — InvitationService.java (new-company
// invite flow) still throws PlanLimitExceededException with the legacy 'max-users' key, while
// InviteAndAddUserToExistingCompanyTenantServiceImpl (existing-company invite) goes through
// LimitCheckService.checkTeamMemberLimit()'s 'team_members' key — same limit, two keys until the
// backend is normalized.
const FEATURE_KEY_MAP: Record<string, PlanLimitDialogInfo> = {
  transport_units: { label: 'Stock Units', manageRoute: '/new-tallysheet/transport-unit/view' },
  tallysheets: { label: 'Tally Sheets', manageRoute: '/new-tallysheet' },
  shipments: { label: 'Consignments', manageRoute: '/consignments' },
  business_partners: { label: 'Business Partners', manageRoute: '/business-partners' },
  products: { label: 'Products', manageRoute: '/products' },
  loading_sites: { label: 'Loading Sites', manageRoute: '/site-locations' },
  team_members: { label: 'Team Members', manageRoute: '/manage/user' },
  'max-users': { label: 'Team Members', manageRoute: '/manage/user' },
  // Photos have no natural "manage" list to send the user to (uploads live per-tally, not on
  // their own screen) — Upgrade-only dialog for this one, deliberately not uniform with the rest.
  photo_uploads: { label: 'Photos', manageRoute: null },
};

@Injectable({ providedIn: 'root' })
export class PlanLimitDialogService {
  private readonly dialogRequestSource = new Subject<PlanLimitDialogRequest>();
  readonly dialogRequest$ = this.dialogRequestSource.asObservable();

  private isOpen = false;

  open(request: PlanLimitDialogRequest): void {
    if (this.isOpen) return;
    this.isOpen = true;
    this.dialogRequestSource.next(request);
  }

  close(): void {
    this.isOpen = false;
  }

  describe(featureKey: string): PlanLimitDialogInfo {
    return FEATURE_KEY_MAP[featureKey] ?? { label: featureKey, manageRoute: null };
  }
}
